// File: src/components/player/FullMusicPlayer.js
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import { useMusic } from "../context/MusicContext";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import TopMenuBar from "../layouts/TopMenuBar";
import BottomMenuBar from "../layouts/BottomMenuBar";
import useSongActions from "../hooks/useSongActions";
import PointsTipModal from "../components/fans/PointsTipModal";
import { getDisplayArtistNames } from "../Utils/song";
import AddToPlaylistModal from "../components/music/AddToPlaylistModal";
import CreatePlaylistModal from "../components/music/CreatePlaylistModal";
import ComingSoonModal from "../components/miscellaneous/ComingSoonModal";
import { useProfile } from "../context/ProfileContext";
import ProfileLink from "../components/profile/ProfileLink";
import toast from "react-hot-toast";
import ReportTrackModal from "../components/miscellaneous/ReportTrackModal";
import DailyCapModal from "../components/trends/DailyCapModal";
import usePressAndHold from "../hooks/usePressAndHold";
import ReactionSelector from "../components/music/ReactionSelector";

import closeIcon from "../assets/close.png";
import playIcon from "../assets/play.png";
import pauseIcon from "../assets/pause.png";
import playDarkmodeIcon from "../assets/play_darkmode.png";
import pauseDarkmodeIcon from "../assets/pause_darkmode.png";
import nextIcon from "../assets/next.png";
import previousIcon from "../assets/previous.png";
import shuffleIcon from "../assets/shuffle.png";
import shuffleOnIcon from "../assets/shuffleon.png";
import repeatIcon from "../assets/repeat.png";
import repeatOnIcon from "../assets/repeaton.png";
import shuffleDarkmodeIcon from "../assets/shuffle_darkmode.png";
import repeatDarkmodeIcon from "../assets/repeat_darkmode.png";
import repeatSongOnIcon from "../assets/repeatsongon.png";
import likeIcon from "../assets/like.png";
import likedIcon from "../assets/trends_liked.png";
import dislikedIcon from "../assets/trends_disliked.png";
import tipIcon from "../assets/tip_icon.png";
import tipColourIcon from "../assets/tip_colour_icon.png";
import saveIcon from "../assets/save.png";
import shareIcon from "../assets/share.png";
import subscribeIcon from "../assets/subscribe_grey.png";
import subscribedIcon from "../assets/subscribe_colour.png";
import likeWhite from "../assets/trends_like.png";
import subscribeWhite from "../assets/subscribe_white.png";
import tipWhite from "../assets/tip_white.png";
import saveWhite from "../assets/trends_save.png";
import shareWhite from "../assets/trends_share.png";

// Reusable Marquee
const MarqueeText = ({ children, className = "" }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflowDistance, setOverflowDistance] = useState(0);

  useEffect(() => {
    const calculateOverflow = () => {
      if (containerRef.current && textRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;

        if (textWidth > containerWidth) {
          setOverflowDistance(textWidth - containerWidth + 12);
        } else {
          setOverflowDistance(0);
        }
      }
    };

    calculateOverflow();
    window.addEventListener("resize", calculateOverflow);
    return () => window.removeEventListener("resize", calculateOverflow);
  }, [children]);

  const duration = Math.max(4, overflowDistance / 35);

  return (
    <div ref={containerRef} className="w-full overflow-hidden whitespace-nowrap relative">
      <motion.div
        ref={textRef}
        className={`inline-block whitespace-nowrap ${className}`}
        animate={
          overflowDistance > 0
            ? { x: [0, 0, -overflowDistance, -overflowDistance, 0] }
            : { x: 0 }
        }
        transition={
          overflowDistance > 0
            ? {
                duration: duration + 4,
                times: [0, 0.2, 0.7, 0.9, 1],
                repeat: Infinity,
                ease: "easeInOut",
              }
            : {}
        }
      >
        {children}
      </motion.div>
    </div>
  );
};

const FullMusicPlayer = () => {
  const {
    currentSong,
    isPlaying,
    togglePlayPause,
    playNext,
    playPrevious,
    isExpanded,
    setIsExpanded,
    audioRef,
    playSource,
    isShuffleOn,
    toggleShuffle,
    repeatMode,
    toggleRepeatMode,
    closePlayer,
  } = useMusic();

  const {
    rating,
    rateSong,
    isSubscribed,
    toggleSubscription,
  } = useSongActions({
    songId: currentSong?.id,
    artistId: currentSong?.artistId,
  });

  const likeControls = useAnimation();
  const subscribeControls = useAnimation();

  const progressBarRef = useRef(null);
  const menuRef = useRef(null);
  const bioRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPercent, setDragPercent] = useState(0);
  const duration = audioRef?.current?.duration || 0;
  const [isPointsTipModalOpen, setIsPointsTipModalOpen] = useState(false);
  const [isAddToPlaylistOpen, setIsAddToPlaylistOpen] = useState(false);
  const { user, isDarkMode, activeProfile } = useProfile();
  const [isCreatePlaylistOpen, setIsCreatePlaylistOpen] = useState(false);
  const [isComingSoonModalOpen, setIsComingSoonModalOpen] = useState(false);
  const [isCapModalOpen, setIsCapModalOpen] = useState(false);
  const [isReactionOpen, setIsReactionOpen] = useState(false);

  // Bio state & fallback
  const [artistBio, setArtistBio] = useState(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [hasBioOverflow, setHasBioOverflow] = useState(false);
  const closeTimerRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [cumulativeSongTip, setCumulativeSongTip] = useState(0);

  const db = getFirestore();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch Artist Bio fallback if song description is not present
  useEffect(() => {
    setIsBioExpanded(false);
    let isMounted = true;

    const fetchArtistBio = async () => {
      // If song already has description, no need to query Firestore
      if (currentSong?.description?.trim() || !currentSong?.artistId) {
        setArtistBio(null);
        return;
      }

      try {
        const artistDoc = await getDoc(doc(db, "artist_accounts", currentSong.artistId));
        if (isMounted && artistDoc.exists()) {
          setArtistBio(artistDoc.data()?.bio?.trim() || null);
        }
      } catch (err) {
        console.error("Error fetching fallback artist bio:", err);
      }
    };

    fetchArtistBio();
    return () => {
      isMounted = false;
    };
  }, [currentSong?.id, currentSong?.artistId, currentSong?.description, db]);

  // Priority: 1) Song Description -> 2) Artist Bio -> 3) None
  const displayBio = currentSong?.description?.trim() || artistBio || null;

  // Check if text exceeds 2 lines
  useLayoutEffect(() => {
    if (!bioRef.current || !displayBio) {
      setHasBioOverflow(false);
      return;
    }
    if (!isBioExpanded) {
      const el = bioRef.current;
      setHasBioOverflow(el.scrollHeight > el.clientHeight);
    }
  }, [displayBio, isBioExpanded]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const startCloseTimer = () => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsReactionOpen(false);
    }, 300);
  };

  const handleOpenPlaylistModal = () => {
    if (activeProfile?.type === "artist") {
      toast.error("Artist accounts cannot add songs to playlists.");
      return;
    }
    setIsAddToPlaylistOpen(true);
  };

  useEffect(() => {
    if (location.pathname.startsWith("/song/")) {
      const pathSegments = location.pathname.split("/song/")[1];
      const urlSongId = pathSegments ? pathSegments.split("/")[0].split("?")[0] : null;

      if (currentSong?.id && urlSongId && String(currentSong.id) !== String(urlSongId)) {
        navigate(`/song/${currentSong.id}`, {
          replace: true,
          state: { fromPlayer: true },
        });
      }
    }
  }, [currentSong?.id, location.pathname, navigate]);

  useEffect(() => {
    if (isExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isExpanded]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
    };
    audio.addEventListener("timeupdate", updateTime);
    return () => audio.removeEventListener("timeupdate", updateTime);
  }, [audioRef, isDragging]);

  useEffect(() => {
    if (!user?.uid || !currentSong?.id) {
      setCumulativeSongTip(0);
      return;
    }

    const tipRef = doc(db, "users", user.uid, "tipsGiven", currentSong.id);
    const unsubscribe = onSnapshot(
      tipRef,
      (tipSnap) => {
        if (tipSnap.exists()) {
          const points =
            tipSnap.data()?.totalPointsTipped || tipSnap.data()?.pointsTipped || 0;
          setCumulativeSongTip(points);
        } else {
          setCumulativeSongTip(0);
        }
      },
      (err) => {
        console.error("Error listening to song tip status:", err);
      }
    );

    return () => unsubscribe();
  }, [currentSong?.id, user?.uid, db]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handleDrag = (clientX) => {
    if (!progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const ratio = x / rect.width;
    const newTime = ratio * duration;
    setDragPercent(ratio * 100);
    setCurrentTime(newTime);
  };

  const handleMouseDown = (e) => {
    if (!audioRef?.current || !duration) return;
    setIsDragging(true);
    handleDrag(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    handleDrag(e.clientX);
  };

  const handleMouseUp = () => {
    if (isDragging && audioRef?.current && duration) {
      audioRef.current.currentTime = (dragPercent / 100) * duration;
    }
    setIsDragging(false);
  };

  const handleExplicitReaction = async (selectedRating) => {
    likeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.3 } });
    const { showCapModal } = await rateSong(selectedRating, "full_player_reaction");
    if (showCapModal) {
      setIsCapModalOpen(true);
    }
  };

  const handleRateToggle = async () => {
    likeControls.start({ scale: [1, 1.4, 1], transition: { duration: 0.3 } });
    const newRating = rating === "liked" ? "disliked" : "liked";
    const { showCapModal } = await rateSong(newRating, "full_player");

    if (showCapModal) {
      setIsCapModalOpen(true);
    }
  };

  const ratingGestureHandlers = usePressAndHold(
    () => {
      cancelCloseTimer();
      setIsReactionOpen(true);
    },
    handleRateToggle,
    250
  );

  const handleSubscribeToggle = async () => {
    if (!currentSong?.artistId) return;

    subscribeControls.start({
      scale: [1, 1.4, 1],
      transition: { duration: 0.3 },
    });

    await toggleSubscription(
      currentSong.artistId,
      currentSong.artistName,
      "full_player"
    );
  };

  const handleTipClick = () => {
    if (!user || user.isAnonymous || activeProfile?.isGuest) {
      sessionStorage.setItem("postAuthRedirect", location.pathname);
      toast("Please sign up to tip points to this artist!");
      navigate("/login");
      return;
    }

    if (activeProfile?.type === "artist") {
      toast.error("Artist accounts cannot boost songs.");
      return;
    }

    if (!currentSong) {
      toast.error("No active song available to boost.");
      return;
    }

    setIsPointsTipModalOpen(true);
  };

  const getPlayPauseIcon = () => {
    if (isPlaying) {
      return isDarkMode ? pauseDarkmodeIcon : pauseIcon;
    }
    return isDarkMode ? playDarkmodeIcon : playIcon;
  };

  const getRepeatIcon = () => {
    if (repeatMode === "playlist") return repeatOnIcon;
    if (repeatMode === "song") return repeatSongOnIcon;
    return repeatIcon;
  };

  const handleNavigateToCreate = () => {
    setIsAddToPlaylistOpen(false);
    setIsCreatePlaylistOpen(true);
  };

  if (!currentSong) return null;

  return (
    <motion.div
      className={`fixed inset-0 z-50 flex flex-col h-screen transition-colors duration-500 ${
        isDarkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-gray-900"
      }`}
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <button
        className={`absolute left-4 z-50 p-2 rounded-xl border transition-all ${
          isDarkMode
            ? "border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-white"
            : "border-gray-200 bg-white/80 text-gray-500 hover:text-black"
        }`}
        style={{ top: "60px" }}
        onClick={closePlayer}
      >
        <img src={closeIcon} alt="Close" className="w-4 h-4" />
      </button>

      <TopMenuBar />

      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto no-scrollbar pt-20 md:pt-6 pb-28 md:pb-24 justify-start md:justify-center">
        <div className="w-full max-w-md md:max-w-4xl mx-auto px-4 mt-2 md:mt-0">
          <div
            className={`p-4 md:p-6 rounded-[2.5rem] border transition-all duration-500 ${
              isDarkMode
                ? "bg-zinc-900 border-zinc-800 shadow-2xl"
                : "bg-gray-50 border-gray-200 shadow-lg"
            }`}
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6">

              {/* ALBUM ART */}
              <div className="relative w-full max-w-[280px] sm:max-w-[320px] md:w-80 md:h-80 aspect-square flex-shrink-0 bg-black rounded-[2rem] overflow-hidden flex items-center justify-center shadow-lg">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentSong.id}
                    src={currentSong.albumArtworkURL}
                    alt="Album Art"
                    className="h-full w-full object-cover"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  />
                </AnimatePresence>
              </div>

              {/* METADATA & CONTROLS */}
              <div className="flex-1 w-full min-w-0 flex flex-col justify-between text-left">
                <div>
                  <div
                    className="w-full flex items-center justify-between mb-2 relative"
                    ref={menuRef}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 truncate">
                      Playing From:{" "}
                      <span className="text-brand-pink font-bold">
                        {playSource || "Library"}
                      </span>
                    </span>

                    <div className="relative ml-auto">
                      <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="p-1 rounded-full text-gray-400 hover:text-white transition-colors focus:outline-none"
                        title="Options"
                      >
                        <span className="text-xs font-bold tracking-widest">•••</span>
                      </button>

                      {isMenuOpen && (
                        <div
                          className={`absolute right-0 top-7 z-50 w-48 rounded-xl border shadow-xl py-1 text-left transition-all ${
                            isDarkMode
                              ? "bg-zinc-950 border-zinc-800 text-white"
                              : "bg-white border-gray-200 text-gray-900"
                          }`}
                        >
                          <button
                            onClick={() => {
                              setIsMenuOpen(false);
                              setIsReportModalOpen(true);
                            }}
                            className="w-full text-left px-4 py-2 text-xs font-semibold hover:bg-red-500/10 hover:text-red-500 transition-colors flex items-center gap-2"
                          >
                            <span>Report Track</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Profile Link & Title */}
                  <div className="flex items-center gap-3.5 mt-2 overflow-hidden">
                    <ProfileLink
                      profileId={currentSong.artistId}
                      photoURL={currentSong.artistPhotoURL}
                      altText={currentSong.artistName}
                      size="w-12 h-12 flex-shrink-0"
                      onClick={() => {
                        setIsExpanded(false);
                        navigate(`/profile/${currentSong.artistId}`);
                      }}
                      source="full_player"
                    />

                    <div className="min-w-0 flex-1 overflow-hidden">
                      <MarqueeText>
                        <h1
                          className={`text-xl md:text-2xl font-bold leading-tight ${
                            isDarkMode ? "text-white" : "text-slate-900"
                          }`}
                        >
                          {currentSong.title || "Untitled"}
                        </h1>
                      </MarqueeText>

                      <MarqueeText className="mt-0.5">
                        <p className="text-sm text-gray-500 dark:text-zinc-400/80 italic">
                          <span className="font-medium text-gray-400 dark:text-zinc-500">
                            by{" "}
                          </span>
                          <span className="font-semibold text-gray-700 dark:text-zinc-200">
                            {getDisplayArtistNames(currentSong)}
                          </span>
                        </p>
                      </MarqueeText>
                    </div>
                  </div>

                  {/* Bio Section: Song Description -> Artist Bio -> None */}
                  {displayBio && (
                    <div className="mt-3.5 border-t border-dashed border-slate-200 dark:border-zinc-800 pt-3 text-xs leading-relaxed text-gray-600 dark:text-zinc-400">
                      <p
                        ref={bioRef}
                        className={`transition-all duration-200 ${
                          isBioExpanded ? "max-h-40 overflow-y-auto pr-1 no-scrollbar" : "line-clamp-2"
                        }`}
                      >
                        {displayBio}
                      </p>
                      {(hasBioOverflow || isBioExpanded) && (
                        <button
                          type="button"
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="mt-1 text-[11px] font-bold text-brand-pink hover:underline focus:outline-none transition-colors block"
                        >
                          {isBioExpanded ? "Show less" : "Read more"}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="mt-4 pt-1">
                  <div className="flex justify-between text-[10px] font-bold text-gray-400 px-0.5 mb-1.5">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>

                  <div className="relative mb-3 h-5">
                    <div
                      ref={progressBarRef}
                      className={`absolute top-1/2 left-0 w-full h-1.5 rounded -translate-y-1/2 cursor-pointer ${
                        isDarkMode ? "bg-zinc-800" : "bg-gray-200"
                      }`}
                      onMouseDown={handleMouseDown}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      onMouseLeave={handleMouseUp}
                      onTouchStart={(e) => handleMouseDown(e.touches[0])}
                      onTouchMove={(e) => handleMouseMove(e.touches[0])}
                      onTouchEnd={handleMouseUp}
                    >
                      <div
                        className="absolute top-0 left-0 h-full bg-[#ff00cc] rounded-full"
                        style={{
                          width: `${
                            isDragging
                              ? dragPercent
                              : (currentTime / duration) * 100 || 0
                          }%`,
                        }}
                      />
                      <div
                        className="absolute w-3.5 h-3.5 bg-white rounded-full border border-gray-300 shadow-md z-10"
                        style={{
                          top: "50%",
                          left: `${
                            isDragging
                              ? dragPercent
                              : (currentTime / duration) * 100 || 0
                          }%`,
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex justify-around items-center mb-3 pt-1">
                  <button onClick={toggleShuffle} className="p-1" title="Shuffle">
                    <img
                      src={
                        isShuffleOn
                          ? shuffleOnIcon
                          : isDarkMode
                          ? shuffleDarkmodeIcon
                          : shuffleIcon
                      }
                      alt="Shuffle"
                      className="w-6 h-6 transition-all duration-200"
                    />
                  </button>

                  <button onClick={playPrevious} className="p-1" title="Previous">
                    <img
                      src={previousIcon}
                      alt="Previous"
                      className={`w-8 h-8 ${isDarkMode ? "invert" : ""}`}
                    />
                  </button>

                  <button
                    onClick={togglePlayPause}
                    className="p-1 hover:scale-105 active:scale-95 transition-transform focus:outline-none"
                    title={isPlaying ? "Pause" : "Play"}
                  >
                    <img
                      src={getPlayPauseIcon()}
                      alt={isPlaying ? "Pause" : "Play"}
                      className="w-12 h-12 sm:w-14 sm:h-14 object-contain select-none"
                    />
                  </button>

                  <button onClick={playNext} className="p-1" title="Next">
                    <img
                      src={nextIcon}
                      alt="Next"
                      className={`w-8 h-8 ${isDarkMode ? "invert" : ""}`}
                    />
                  </button>

                  <button onClick={toggleRepeatMode} className="p-1" title="Repeat">
                    <img
                      src={
                        repeatMode !== "off"
                          ? getRepeatIcon()
                          : isDarkMode
                          ? repeatDarkmodeIcon
                          : repeatIcon
                      }
                      alt="Repeat"
                      className="w-6 h-6 transition-all duration-200"
                    />
                  </button>
                </div>

                {/* Actions */}
                <div className="flex justify-around items-center pt-3 border-t border-slate-200 dark:border-zinc-800 w-full">
                  <div
                    className="relative flex items-center justify-center"
                    onMouseEnter={cancelCloseTimer}
                    onMouseLeave={startCloseTimer}
                  >
                    <ReactionSelector
                      isOpen={isReactionOpen}
                      onClose={() => setIsReactionOpen(false)}
                      onSelectReaction={handleExplicitReaction}
                      onMouseEnter={cancelCloseTimer}
                      isDarkMode={isDarkMode}
                      position="top"
                    />

                    <motion.button
                      {...ratingGestureHandlers}
                      animate={likeControls}
                      className="p-2 flex items-center justify-center transition-transform active:scale-90"
                      title="Rate"
                    >
                      <img
                        src={
                          rating === "liked"
                            ? likedIcon
                            : rating === "disliked"
                            ? dislikedIcon
                            : isDarkMode
                            ? likeWhite
                            : likeIcon
                        }
                        alt="Rate"
                        className="w-5 h-5 object-contain"
                      />
                    </motion.button>
                  </div>

                  <motion.button
                    onClick={handleSubscribeToggle}
                    animate={subscribeControls}
                    className="p-2 flex items-center justify-center transition-transform active:scale-90"
                    title="Follow Artist"
                  >
                    <img
                      src={
                        isSubscribed
                          ? subscribedIcon
                          : isDarkMode
                          ? subscribeWhite
                          : subscribeIcon
                      }
                      alt="Subscribe"
                      className="w-5 h-5 object-contain"
                    />
                  </motion.button>

                  <motion.button
                    onClick={handleTipClick}
                    className="p-2 flex items-center justify-center transition-transform active:scale-90"
                    title="Boost Song"
                  >
                    <img
                      src={
                        cumulativeSongTip > 0
                          ? tipColourIcon
                          : isDarkMode
                          ? tipWhite
                          : tipIcon
                      }
                      alt="Tip"
                      className="w-5 h-5 object-contain"
                    />
                  </motion.button>

                  <motion.button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPlaylistModal();
                    }}
                    className="p-2 flex items-center justify-center transition-transform active:scale-90"
                    title="Add to Playlist"
                  >
                    <img
                      src={isDarkMode ? saveWhite : saveIcon}
                      alt="Add to Playlist"
                      className="w-5 h-5 object-contain"
                    />
                  </motion.button>

                  <button
                    onClick={() => setIsComingSoonModalOpen(true)}
                    className="p-2 flex items-center justify-center transition-transform active:scale-90"
                    title="Share"
                  >
                    <img
                      src={isDarkMode ? shareWhite : shareIcon}
                      alt="Share"
                      className="w-5 h-5 object-contain"
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomMenuBar onNavigate={() => setIsExpanded(false)} />

      <ReportTrackModal
        song={currentSong}
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        isDarkMode={isDarkMode}
      />

      <AddToPlaylistModal
        isOpen={isAddToPlaylistOpen}
        onClose={() => setIsAddToPlaylistOpen(false)}
        songToAdd={currentSong}
        onNavigateToCreate={handleNavigateToCreate}
      />

      <CreatePlaylistModal
        isOpen={isCreatePlaylistOpen}
        onClose={() => setIsCreatePlaylistOpen(false)}
        songToAdd={currentSong}
        source="add_to_playlist_modal"
      />

      <PointsTipModal
        isOpen={isPointsTipModalOpen}
        onClose={() => setIsPointsTipModalOpen(false)}
        songToTip={currentSong}
        isDarkMode={isDarkMode}
      />

      <ComingSoonModal
        isOpen={isComingSoonModalOpen}
        onClose={() => setIsComingSoonModalOpen(false)}
        isDarkMode={isDarkMode}
      />

      <DailyCapModal
        isOpen={isCapModalOpen}
        onClose={() => setIsCapModalOpen(false)}
        isDarkMode={isDarkMode}
      />
    </motion.div>
  );
};

export default FullMusicPlayer;